<template>
  <div class="cash-market-filters">
    <div>
      <Heading :level="3">Ativos à vista</Heading>
    </div>
    <div class="cash-market-filters__content">
      <template v-if="isLoading">
        <div
          class="cash-market-filters__content__left cash-market-filters__content__loader"
        >
          <HeaderFilterLoader
            v-for="index in 2"
            :key="index"
            class="cash-market-filters__filter-option"
          />
          <HeaderSubtitleLoader />
        </div>
        <HeaderFilterLoader />
      </template>
      <template v-else>
        <div class="cash-market-filters__content__left">
          <div v-if="hasDateOptions">
            <FilterOption
              v-for="option in dateOptions"
              :key="option.value"
              :label="option.label"
              class="cash-market-filters__filter-option"
              :value="option.value"
              :checked="
                option.value === filters[CashMarketFiltersTypes.Date].selected
              "
              @change="
                filter =>
                  handleEmitOnFilterChange(CashMarketFiltersTypes.Date, filter)
              "
            />
          </div>
          <BaseText
            v-if="updatedAt"
            size="xs"
            :color="NebraskaColors.textSecondary"
          >
            {{ updatedAt.formatted }}
          </BaseText>
        </div>
        <div class="cash-market-filters__content__right">
          <FilterSelect
            v-if="hasInstrumentTypesOptions"
            class="cash-market-filters__select"
            :options="instrumentTypesOptions"
            :value="filters[CashMarketFiltersTypes.Type].selected"
            label="Tipo"
            data-testid="instrument-type-filter"
            use-option-label
            @change="
              filter =>
                handleEmitOnFilterChange(CashMarketFiltersTypes.Type, filter)
            "
          />
          <ButtonSecondary
            class="cash-market-filters__button"
            label="Ver alocação"
            @click="emitShowAllocation"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import {
  Component,
  Vue,
  Prop,
  Emit,
  InjectReactive
} from 'vue-property-decorator';

import {
  FilterOption,
  BaseText,
  FilterSelect,
  IFilterOption,
  SkeletonLoader,
  Heading,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';
import {
  HeaderSubtitleLoader,
  HeaderFilterLoader
} from '@/modules/trade/views/TradePositionsView/components/Loaders';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { ICashMarketFilters, CashMarketFiltersTypes } from '../../types';
import { NumberFormatted, StringFormatted } from '@/modules/trade/types';

@Component({
  components: {
    BaseText,
    FilterOption,
    FilterSelect,
    SkeletonLoader,
    Heading,
    HeaderSubtitleLoader,
    HeaderFilterLoader,
    ButtonSecondary
  }
})
export default class CashMarketFilters extends Vue {
  @Prop({ type: Object })
  readonly updatedAt!: NumberFormatted;

  @Prop({ type: Boolean, default: false })
  readonly isLoading!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly error!: boolean;

  @InjectReactive('filters')
  readonly filters!: ICashMarketFilters;

  @Emit('on-filter-change')
  onFilterChange(_filterType: CashMarketFiltersTypes, _filter: IFilterOption) {}

  @Emit('show-allocation')
  emitShowAllocation() {}

  readonly NebraskaColors = NebraskaColors;
  readonly CashMarketFiltersTypes = CashMarketFiltersTypes;

  get updatedAtFormatted() {
    return this.updatedAt?.formatted || '-';
  }

  get hasInstrumentTypesOptions() {
    return this.filters[CashMarketFiltersTypes.Type].available?.length > 0;
  }

  get instrumentTypesOptions(): IFilterOption[] {
    return this.filters[CashMarketFiltersTypes.Type].available.map(
      this.formatFilterOption
    );
  }

  get hasDateOptions() {
    return this.filters[CashMarketFiltersTypes.Date].available?.length > 0;
  }

  get dateOptions(): IFilterOption[] {
    return this.filters[CashMarketFiltersTypes.Date].available.map(
      this.formatFilterOption
    );
  }

  handleEmitOnFilterChange(
    filterKey: CashMarketFiltersTypes,
    filterValue?: string | number
  ) {
    const filter = this.filters[filterKey].available.find(
      ({ value }) => value === filterValue
    ) as StringFormatted;

    this.onFilterChange(filterKey, this.formatFilterOption(filter));
  }

  formatFilterOption(option: StringFormatted): IFilterOption {
    return {
      value: option.value,
      label: option.formatted
    };
  }
}
</script>

<style lang="less" scoped>
.cash-market-filters {
  &__content {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__left {
      display: flex;
      align-items: center;
      margin-top: @size-spacing-x300;
    }

    &__right {
      display: flex;
      align-items: center;
    }

    &__loader {
      margin: @size-spacing-x300 0;
    }
  }

  &__filter-option {
    margin-right: @size-spacing-x300;
  }

  &__select {
    margin-left: @size-spacing-x300;
  }

  &__button {
    margin-left: @size-spacing-x300;
  }
}
</style>
